import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useMyMembership } from "@/hooks/useMyMembership";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { X, CreditCard } from "lucide-react";
import { FORM_CONTROLS, RESPONSIVE, TYPOGRAPHY } from "@/design-system";
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LCommon } from "@/components/i18n";
import { ROUTES } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import { logger } from "@/utils/logger";

const DISMISS_KEY_PREFIX = "payment-failed-dismissed-";
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;


export function PaymentFailedBanner() {
  const { user } = useAuth();
  const { lCommon } = useLocalizations();
  const { data: membership } = useMyMembership();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!user || !membership) {
      setIsVisible(false);
      return;
    }

    if (membership.status !== "paused") {
      setIsVisible(false);
      return;
    }

    try {
      const key = `${DISMISS_KEY_PREFIX}${user.id}`;
      const dismissedTimestamp = localStorage.getItem(key);

      if (dismissedTimestamp) {
        const elapsed = Date.now() - parseInt(dismissedTimestamp);
        setIsVisible(elapsed >= TWENTY_FOUR_HOURS);
      } else {
        setIsVisible(true);
      }
    } catch (err) {
      logger.error("PaymentFailedBanner: localStorage error", err);
      setIsVisible(true);
    }
  }, [user, membership]);

  const handleDismiss = () => {
    if (user) {
      try {
        localStorage.setItem(`${DISMISS_KEY_PREFIX}${user.id}`, Date.now().toString());
      } catch (err) {
        logger.error("PaymentFailedBanner: failed to persist dismiss", err);
      }
    }
    setIsVisible(false);
  };

  const handleUpdatePayment = () => {
    navigate(ROUTES.ACCOUNT_MEMBERSHIP_METHODS);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="border-b border-destructive/50">
      <div className="container px-4">
        <Alert variant="destructive" className="border-0 bg-transparent py-3 px-0 my-0">
          <div className="flex items-start gap-3">
            <CreditCard className="h-4 w-4 shrink-0 mt-0.5" />
            <AlertDescription className="flex-1">
              <div className={RESPONSIVE.headerWithActionsGap3}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium">
                    <LCommon
                      id="payment.failed_banner.message"
                      defaultValue="Your last payment failed. Update your payment method to keep your access."
                    />
                  </span>
                </div>

                <div className={cn(RESPONSIVE.flexRowCenterGap2, "shrink-0")}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUpdatePayment}
                    className="h-8 flex-1 sm:flex-none"
                  >
                    <LCommon
                      id="payment.failed_banner.cta"
                      defaultValue="Update payment method"
                    />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDismiss}
                    className={cn(FORM_CONTROLS.button.iconTouch, "shrink-0")}
                    aria-label={lCommon("payment.failed_banner.dismiss", {
                      defaultValue: "Dismiss payment alert",
                    })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </div>
        </Alert>
      </div>
    </div>
  );
}
